import React, { useState } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputSwitch } from "primereact/inputswitch";
import Loader from "../components/Loader.js";

const CotizacionActions = ({
  cotizacion,
  loading,
  loadingAsig,
  handleAgregarProducto,
  openDialog,
  handleShowResumenMensual,
  guardarCotizacion,
  checkDatosFaltantesCliente,
  handleTerminar,
  handleRechazar,
  cerrarContrato,
  diasAviso,
  setDiasAviso,
  cantidadDiasAvisoContrato,
  diasFirma,
  setDiasFirma,
  cantidadDiasFirmaContrato,
  showCerrarContrato,
  setShowCerrarContrato,
  handleShowRenovarAsignacion,
  handleShowModificarAsignacion,
  documentos,
  detallesCobro,
  setDetallesCobro,
  handleTablaChange,
  cobroCounts,
  flattenedDataArriendo,
  servicios,
}) => {
  const trimmedDocumentos = documentos
    ? documentos.map((doc) => doc.trim())
    : [];

  function groupByIdComboAndDescriptionPerCobroLocal(data) {
    const grouped = [];
    const categorias = ["Factura Afecta", "Factura Exenta", "Nota de Cobro"];
    categorias.forEach((cat) => {
      const itemsCat = data.filter((i) => i.cobro === cat);
        grouped.push(...itemsCat);
      });
    return grouped;
  }

  const detallesCobroAgrupados = React.useMemo(() => {
      return groupByIdComboAndDescriptionPerCobroLocal(detallesCobro);
  }, [detallesCobro]);

  const categorias = ["Factura Afecta", "Factura Exenta", "Nota de Cobro"];
  const globalOrdered = React.useMemo(() => {
    let arr = [];
    categorias.forEach(cat => {
      arr.push(
        ...detallesCobro
          .filter(i => i.cobro === cat)
          .sort((a, b) => a.descripcion.localeCompare(b.descripcion))
      );
    });
    return arr;
  }, [detallesCobro]);

  const [actions, setActions] = useState(false);

  const handleAsignar = async () => {
      // Siempre respetar lo que viene en detallesCobro
      const finalItems = detallesCobro.map(item => {
        const isConsolidado = !!item.consolidado;
        const hasAgrupamiento = Number(item.agrupamiento) > 0;

        let agrup = item.agrupamiento;

        if (isConsolidado && !hasAgrupamiento) {
          // Si está consolidado pero sin número de agrupamiento, asignar por posición
          agrup = globalOrdered.findIndex(i => i.id === item.id) + 1;
        }

        if (!isConsolidado && !hasAgrupamiento) {
          // Si no está consolidado, no forzar agrupamiento para evitar "grupos fantasmas"
          agrup = 0;
        }

        return {
          ...item,
          agrupamiento: agrup
        };
      });

      setActions(false);

      finalItems.forEach(item => {
        const source = flattenedDataArriendo.some(x => x.id === item.id)
          ? 'flattened'
          : servicios.some(x => x.id === item.id)
            ? 'servicios'
            : 'unicos';

        // Persistir consolidado y agrupamiento
        handleTablaChange(item, item.consolidado, source, 'consolidado');
        handleTablaChange(item, item.agrupamiento, source, 'agrupamiento');
      });
      //console.log("finalItems a enviar", finalItems);
      cerrarContrato(finalItems);
    };

  const optionsByCobro = React.useMemo(() => {
    const map = {};
    let offset = 0;
    categorias.forEach(cat => {
      const itemsCat = detallesCobro.filter(i => i.cobro === cat);
      const consolidados = itemsCat.filter(i => i.consolidado);
      const totalSlots = itemsCat.length;
      map[cat] = Array.from({ length: totalSlots }, (_, idx) => ({
        label: `N° ${offset + idx + 1}`,
        value: offset + idx + 1
      }));
      offset += totalSlots;
    });
    return map;
  }, [detallesCobro]);

  function handleValidar(){
    const consolidados = detallesCobro.filter(i => i.consolidado);
    const gruposUnicos = Array.from(new Set(
      consolidados.map(i =>
        i.agrupamiento > 0
          ? i.agrupamiento
          : (globalOrdered.findIndex(item => item.id === i.id) + 1)
      )
    ));
    if (gruposUnicos.some(g =>
        consolidados.filter(i =>
          (i.agrupamiento > 0
            ? i.agrupamiento
            : (globalOrdered.findIndex(item => item.id === i.id) + 1)
          ) === g
        ).length < 2
      )) {
        window.alert("⚠️ Error de agrupación: debes seleccionar al menos 2 ítems para un mismo documento.");
      return;
    }else{
      if (window.confirm("✅ Configuración válida: todos los grupos cumplen las reglas. ¿Deseas guardar los cambios?")) {
        handleAsignar();
      }
    }
  }

  function handleLimpiarAgrupaciones(){
    if (window.confirm("¿Está seguro que desea reiniciar la configuración?")) {
      const resetDetalles = detallesCobro.map(item => ({
        ...item,
        consolidado: false,
        agrupamiento: 0
      }));
      setDetallesCobro(resetDetalles);
    }
  }

  function onToggleConsolidado(rowData, nuevoValor) {
    onToggleConsolidadoOriginal(rowData, nuevoValor, 'consolidado');
  }

  function onToggleAgrupamiento(rowData, e) {
      setActions(true);
      // Actualizar estado local para feedback inmediato en UI
      setDetallesCobro(prev =>
        prev.map(i => i.id === rowData.id ? { ...i, agrupamiento: e.value } : i)
      );
      // Persistir en la fuente correspondiente
      handleTablaChange(
        rowData,
        e.value,
        flattenedDataArriendo.some(item => item.id === rowData.id)
          ? "flattened"
          : servicios.some(item => item.id === rowData.id)
          ? "servicios"
          : "unicos",
        "agrupamiento"
      );
  }

  function onToggleConsolidadoOriginal(rowData, nuevoValor, campo = 'consolidado') {
    setActions(true);
    const itemsAfectados = [rowData];

    // Persistir en la fuente correspondiente
    itemsAfectados.forEach(item => {
      const source = flattenedDataArriendo.some(x => x.id === item.id)
        ? 'flattened'
        : servicios.some(x => x.id === item.id)
          ? 'servicios'
          : 'unicos';
      handleTablaChange(item, nuevoValor, source, campo);
    });
  }

  const parseFechaDMY = (str) => {
        if (!str) return null;
        const [dia, mes, anio] = String(str).trim().split('/').map(Number);
        if (!dia || !mes || !anio) return null;
        const d = new Date(anio, mes - 1, dia);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const fechaActual = new Date();
    fechaActual.setHours(0, 0, 0, 0);

    const mostrarModificarAsignacion = (() => {
        const hasta = parseFechaDMY(cotizacion?.hasta);

        if (hasta) {
            return fechaActual <= hasta;
        }
        return false;
    })();

    const mostrarRenovarAsignacion = (() => {
        const desde = parseFechaDMY(cotizacion?.desde);

        // Nueva regla: si 'desde' es mayor a hoy, no mostramos
        if (desde && fechaActual < desde) return false;

        // Solo 'desde': mostrar si hoy es mayor o igual a 'desde'
        if (desde) return fechaActual >= desde;

        return false;
    })();

  const estadoActual = cotizacion?.estado?.estado;
    const estadoValido = Boolean(estadoActual);
    const bloquearCotizacion = new Set(["ASIGNACIÓN","ANULADA","REVISIÓN","RECHAZADA","RENOVACIÓN"]);
    const bloquearAprobacion = new Set(["ASIGNACIÓN","ANULADA","RECHAZADA","COTIZACIÓN"]);

    return (
    <div className="p-fluid formgrid grid">
      {
        estadoValido && !bloquearCotizacion.has(estadoActual) && 
      (
        <>
          <div
            className="col-12 md:col-2"
            style={{ display: "flex", alignItems: "stretch" }}
          >
            <Button
              label="Agregar Producto"
              icon="pi pi-box"
              className="p-button-secondary mt-4 mb-4 p-button-rounded p-button"
              style={{ width: "100%" }}
              onClick={handleAgregarProducto}
              disabled={loading}
              raised
            />
          </div>
          <div
            className="col-12 md:col-2"
            style={{ display: "flex", alignItems: "stretch" }}
          >
            <Button
              label="Agregar Subproducto"
              icon="pi pi-box"
              className="p-button-warning mt-4 mb-4 p-button-rounded p-button"
              onClick={() => openDialog("subproducto")}
              style={{ width: "100%", color: "white" }}
              disabled={loading}
              raised
            />
          </div>
          <div
            className="col-12 md:col-2"
            style={{ display: "flex", alignItems: "stretch" }}
          >
            <Button
              label="Agregar Servicio"
              icon="pi pi-wrench"
              className="p-button-info mt-4 mb-4 p-button-rounded p-button"
              onClick={() => openDialog("servicio")}
              style={{ width: "100%" }}
              disabled={loading}
              raised
            />
          </div>
          <div
            className="col-12 md:col-2"
            style={{ display: "flex", alignItems: "stretch" }}
          >
            <Button
              label="Ver Resumen Mensual"
              icon="pi pi-calendar"
              onClick={handleShowResumenMensual}
              className="p-button-help mt-4 mb-4 p-button-rounded p-button"
              style={{ width: "100%" }}
              disabled={loading}
              raised
            />
          </div>
          <div
            className="col-12 md:col-2"
            style={{ display: "flex", alignItems: "stretch" }}
          >
            <Button
              label="Guardar Cotización"
              icon="pi pi-save"
              className="p-button-primary mt-4 mb-4 p-button-rounded p-button"
              onClick={guardarCotizacion}
              style={{ width: "100%" }}
              disabled={loading}
              raised
            />
          </div>
          <div
            className="col-12 md:col-2"
            style={{ display: "flex", alignItems: "stretch" }}
          >
            <Button
              label="Terminar"
              icon="pi pi-check"
              className="p-button-success mt-4 mb-4 p-button-rounded p-button"
              onClick={handleTerminar}
              style={{ width: "100%" }}
              disabled={loading}
              raised
            />
          </div>
        </>
      )}
      {
        estadoValido && !bloquearAprobacion.has(estadoActual) && 
      (
        <>
          {/* Primera fila - botones expandidos */}
          <div className="col-12 md:col-2" style={{ display: "flex", alignItems: "stretch" }}>
            <Button
              label="Agregar Producto"
              icon="pi pi-box"
              className="p-button-secondary mt-4 mb-4 p-button-rounded p-button"
              style={{ width: "100%" }}
              onClick={handleAgregarProducto}
              disabled={loading}
              raised
            />
          </div>
          <div className="col-12 md:col-2" style={{ display: "flex", alignItems: "stretch" }}>
            <Button
              label="Agregar Subproducto"
              icon="pi pi-box"
              className="p-button-warning mt-4 mb-4 p-button-rounded p-button"
              onClick={() => openDialog("subproducto")}
              style={{ width: "100%", color: "white" }}
              disabled={loading}
              raised
            />
          </div>
          <div className="col-12 md:col-2" style={{ display: "flex", alignItems: "stretch" }}>
            <Button
              label="Agregar Servicio"
              icon="pi pi-wrench"
              className="p-button-info mt-4 mb-4 p-button-rounded p-button"
              onClick={() => openDialog("servicio")}
              style={{ width: "100%" }}
              disabled={loading}
              raised
            />
          </div>
          <div className="col-12 md:col-3" style={{ display: "flex", alignItems: "stretch" }}>
            <Button
              label="Ver Resumen Mensual"
              icon="pi pi-calendar"
              onClick={handleShowResumenMensual}
              className="p-button-help mt-4 mb-4 p-button-rounded p-button"
              style={{ width: "100%" }}
              disabled={loading}
              raised
            />
          </div>
          <div className="col-12 md:col-3" style={{ display: "flex", alignItems: "stretch" }}>
            <Button
              label="Guardar Cotización"
              icon="pi pi-save"
              className="p-button-primary mt-4 mb-4 p-button-rounded p-button"
              onClick={guardarCotizacion}
              style={{ width: "100%" }}
              disabled={loading}
              raised
            />
          </div>
          
          {/* Segunda fila - botones centrados */}
          <div className="col-12" style={{ display: "flex", justifyContent: "center", gap: "1rem" }}>
            <Button
              label="Rechazar"
              icon="pi pi-times"
              className="p-button-danger mt-4 mb-4 p-button-rounded p-button"
              onClick={handleRechazar}
              style={{ minWidth: "150px" }}
              disabled={loading}
              raised
            />
            <Button
              label="Aprobar"
              icon="pi pi-check"
              className="p-button-success mt-4 mb-4 p-button-rounded p-button"
              onClick={checkDatosFaltantesCliente}
              style={{ minWidth: "150px" }}
              disabled={loading}
              raised
            />
          </div>

          {/* Dialog para cerrar contrato */}
          <Dialog
            visible={showCerrarContrato}
            onHide={() => setShowCerrarContrato(false)}
            modal
            className="dialog-responsive"
            style={{ maxWidth: window.innerWidth <= 600 ? "95vw" : "60vw", width: window.innerWidth <= 600 ? "95%" : "90%", margin: window.innerWidth <= 600 ? "2.5vw" : "auto"}}
            closable={false}
            onShow={() => {
              // Solo reiniciar si los datos no tienen valores previos de agrupamiento/consolidado
              const hasExistingGrouping = detallesCobro.some(item => 
                item.consolidado === true || item.agrupamiento > 0
              );
              
              if (!hasExistingGrouping) {
                const resetDetalles = detallesCobro.map(item => ({
                  ...item,
                  consolidado: item.consolidado || false,
                  agrupamiento: item.agrupamiento || 0
                }));
                setDetallesCobro(resetDetalles);
              }
            }}
          >
            <>
              {loadingAsig ? (
                <>
                  <Loader />
                </>
              ) : (
                <>
                  <div className="p-fluid formgrid grid responsive-dialog-content">
                    <div className="col-12 md:col-12" style={{ textAlign: "center", position: "relative" }}>

                      <Button
                        icon="pi pi-refresh"
                        className="p-button-secondary p-button-rounded"
                        onClick={handleLimpiarAgrupaciones}
                        style={{ position: "absolute", right: "1rem", top: "1rem"}}
                        tooltip="Reiniciar configuración"
                        tooltipOptions={{ position: 'left' }}
                        disabled={loadingAsig}
                      />

                      <h2 style={{ color: "#42A5F5" }}>Cerrando Cotización</h2>

                      <p style={{ marginTop: "10px", fontSize: "16px" }}>¿Con cuántos días de anticipación se debe notificar el vencimiento del contrato?</p>
                      <span className="p-float-label mt-2 mb-3">
                        <Dropdown
                          id="daysDropdown"
                          value={diasAviso}
                          options={cantidadDiasAvisoContrato}
                          onChange={(e) => setDiasAviso(e.value)}
                          optionLabel="label"
                          placeholder="Seleccionar"
                          disabled={loadingAsig}
                          style={{ width: "100%", marginTop: "6px" }}
                        />
                        <label htmlFor="daysSelect"> Plazo para aviso (días)</label>
                      </span>

                      <p style={{ marginTop: "10px", fontSize: "16px" }}>Defina la cantidad de días disponibles para completar la firma de este contrato. </p>
                      <span className="p-float-label mt-2 mb-3">
                        <Dropdown
                          id="daysDropdown"
                          value={diasFirma}
                          options={cantidadDiasFirmaContrato}
                          onChange={(e) => setDiasFirma(e.value)}
                          optionLabel="label"
                          placeholder="Seleccionar días"
                          style={{ width: "100%", marginTop: "6px" }}
                        />
                        <label htmlFor="daysDropdown"> Plazo para firma (días)</label>
                      </span>
                      
                      

                      {/* agrupacion de documentos */}
                      <fieldset style={{border: "1px solid #ccc",padding: "10px",borderRadius: "5px",marginTop: "20px", }}>
                        <legend>Agrupación de Documentos</legend>

                        {/* ============================================================================== */}
                        {/*                           TABLA FACTURA AFECTA */}
                        {/* ============================================================================== */}

                        <div className="p-field" style={{display: trimmedDocumentos.includes("Factura Afecta")  ? "flex"  : "none",alignItems: "center",}}>
                          <DataTable
                            header={<span><i className="pi pi-file" style={{ marginRight: "10px", color: "#42A5F5" }}></i>Facturas Afectas</span>}
                            dataKey="id"
                            style={{ width: "100%" }}
                            showGridlines
                            stripedRows="true"
                            value={
                              detallesCobroAgrupados
                                .filter(det => det.cobro === "Factura Afecta")
                                .sort((a, b) => {
                                  const aVal = a.agrupamiento > 0 ? a.agrupamiento : globalOrdered.findIndex(i => i.id === a.id) + 1;
                                  const bVal = b.agrupamiento > 0 ? b.agrupamiento : globalOrdered.findIndex(i => i.id === b.id) + 1;
                                  return aVal - bVal;
                                })
                            }
                            loading={loadingAsig}
                            sortField="descripcion"
                            sortOrder={1}
                            className="p-datatable-gridlines"
                          >
                            <Column
                              field="descripcion"
                              header="Nombre"
                              style={{width: "30%"}}
                              body={(rowData) => {
                                const tramo = rowData.tramo && rowData.tramo !== "null" ? ` (${rowData.tramo})` : "";
                                return `${rowData.descripcion}${tramo}`;
                              }}
                            />

                            <Column
                              field="relacion"
                              header="Relación"
                              style={{ width: "15%" }}
                              body={(rowData) => {
                                if (rowData.relacion === 'producto' || rowData.relacion === 'subproducto') {
                                  return 'Arriendo';
                                } else if (
                                  rowData.descripcion?.toLowerCase().includes('habilitacion') ||
                                  rowData.descripcion?.toLowerCase().includes('obra civil')
                                ) {
                                  return 'Servicio único';
                                } else {
                                  return 'Servicio';
                                }
                              }}
                            />

                            <Column
                              field="consolidado"
                              header="Doc. Unificado"
                              body={(rowData) => (
                                <div className="flex justify-content-center">
                                  <span style={{ fontSize: '12px', marginRight: '6px' }}>
                                    {!rowData.consolidado ? 'NO' : ' '}
                                  </span>
                                  <InputSwitch
                                    checked={rowData.consolidado}
                                    onChange={e => onToggleConsolidado(rowData, e.value)}
                                    disabled={cobroCounts[rowData.cobro] === 1}
                                  />
                                  <span style={{ fontSize: '12px', marginLeft: '6px' }}>
                                    {rowData.consolidado ? 'SI' : ' '}
                                  </span>
                                </div>
                              )}
                            />

                            <Column
                              field="agrupamiento"
                              header="N° Documento"
                              body={(rowData) => (
                                <div className="flex justify-content-center">
                                  <Dropdown
                                    value={
                                        (rowData.agrupamiento > 0 ? rowData.agrupamiento : null)
                                    }
                                    options={optionsByCobro[rowData.cobro]}
                                    onChange={(e) => onToggleAgrupamiento(rowData, e)}
                                    placeholder="Seleccionar"
                                    disabled={
                                      cobroCounts[rowData.cobro] === 1 ||
                                      (!rowData.consolidado && detallesCobro.filter(i => i.agrupamiento === rowData.agrupamiento).length === 1)||
                                      (!rowData.consolidado)
                                    }
                                  />
                                </div>
                              )}
                            />
                          </DataTable>
                        </div>

                        {/* ============================================================================== */}
                        {/*                            TABLA FACTURA EXENTA */}
                        {/* ============================================================================== */}

                        <div
                          className="p-field"
                          style={{
                            marginTop: "10px",
                            display: trimmedDocumentos.includes("Factura Exenta")
                              ? "flex"
                              : "none",
                            alignItems: "center",
                          }}
                        >
                          <DataTable
                            header={
                              <span>
                                <i className="pi pi-file" style={{ marginRight: "10px", color: "#42A5F5" }}></i>
                                Facturas Exentas
                              </span>
                            }
                            dataKey="id"
                            style={{ width: "100%" }}
                            showGridlines
                            stripedRows="true"
                            value={
                              detallesCobroAgrupados
                                .filter(det => det.cobro === "Factura Exenta") 
                                .sort((a, b) => {
                                  const aVal = a.agrupamiento > 0 ? a.agrupamiento : globalOrdered.findIndex(i => i.id === a.id) + 1;
                                  const bVal = b.agrupamiento > 0 ? b.agrupamiento : globalOrdered.findIndex(i => i.id === b.id) + 1;
                                  return aVal - bVal;
                                })
                            }
                            loading={loadingAsig}
                            sortField="descripcion"
                            sortOrder={1}
                          >
                            <Column
                              field="descripcion"
                              header="Nombre"
                              style={{ 
                                width: "30%"
                              }}
                              body={(rowData) => {
                                const tramo = rowData.tramo && rowData.tramo !== "null" ? ` (${rowData.tramo})` : "";
                                return `${rowData.descripcion}${tramo}`;
                              }}
                            />

                            <Column
                              field="relacion"
                              header="Relación"
                              style={{ width: "15%" }}
                              body={(rowData) => {
                                if (rowData.relacion === 'producto' || rowData.relacion === 'subproducto') {
                                  return 'Arriendo';
                                } else if (
                                  rowData.descripcion?.toLowerCase().includes('habilitacion') ||
                                  rowData.descripcion?.toLowerCase().includes('obra civil')
                                ) {
                                  return 'Servicio único';
                                } else {
                                  return 'Servicio';
                                }
                              }}
                            />

                            <Column
                              field="consolidado"
                              header="Doc. Unificado"
                              body={(rowData) => (
                                <div className="flex justify-content-center">
                                  <span style={{ fontSize: '12px', marginRight: '6px' }}>
                                    {!rowData.consolidado ? 'NO' : ' '}
                                  </span>
                                
                                  <InputSwitch
                                    checked={rowData.consolidado}
                                    onChange={e => onToggleConsolidado(rowData, e.value)}
                                    disabled={cobroCounts[rowData.cobro] === 1}
                                  />

                                  <span style={{ fontSize: '12px', marginLeft: '6px' }}>
                                    {rowData.consolidado ? 'SI' : ' '}
                                  </span>

                                </div>
                              )}
                            />
                          
                            <Column
                              field="agrupamiento"
                              header="N° Documento"
                              body={(rowData) => (
                                <div className="flex justify-content-center">
                                  <Dropdown
                                    value={
                                      (rowData.agrupamiento > 0 ? rowData.agrupamiento : null)
                                    }
                                    options={optionsByCobro[rowData.cobro]}
                                    onChange={(e) => onToggleAgrupamiento(rowData, e)}
                                    placeholder="Seleccionar"
                                    disabled={
                                      cobroCounts[rowData.cobro] === 1 ||
                                      (!rowData.consolidado && detallesCobro.filter(i => i.agrupamiento === rowData.agrupamiento).length === 1)||
                                      (!rowData.consolidado)
                                    }
                                  />
                                </div>
                              )}
                            />
                          </DataTable>
                        </div>

                        {/* ============================================================================== */}
                        {/*                              TABLA NOTA DE COBRO */}
                        {/* ============================================================================== */}

                        <div
                          className="p-field"
                          style={{
                            marginTop: "10px",
                            display: trimmedDocumentos.includes("Nota de Cobro")
                              ? "flex"
                              : "none",
                            alignItems: "center",
                          }} 
                        >
                          <DataTable
                            header={<span><i className="pi pi-file" style={{ marginRight: "10px", color: "#42A5F5" }}></i>Notas de Cobro</span>}
                            dataKey="id"
                            style={{ width: "100%" }}
                            showGridlines
                            stripedRows="true"
                            value={
                              detallesCobroAgrupados
                                .filter(det => det.cobro === "Nota de Cobro")
                                .sort((a, b) => {
                                  const aVal = a.agrupamiento > 0 ? a.agrupamiento : globalOrdered.findIndex(i => i.id === a.id) + 1;
                                  const bVal = b.agrupamiento > 0 ? b.agrupamiento : globalOrdered.findIndex(i => i.id === b.id) + 1;
                                  return aVal - bVal;
                                })
                            }
                            loading={loadingAsig}
                            sortField="descripcion"
                            sortOrder={1}
                          >
                            <Column
                              field="descripcion"
                              header="Nombre"
                              style={{ 
                                width: "30%" 
                              }}
                              body={(rowData) => {
                                const tramo = rowData.tramo && rowData.tramo !== "null" ? ` (${rowData.tramo})` : "";
                                return `${rowData.descripcion}${tramo}`;
                              }}
                            />
                            
                            <Column
                              field="relacion"
                              header="Relación"
                              style={{ width: "15%" }}
                              body={(rowData) => {
                                if (rowData.relacion === 'producto' || rowData.relacion === 'subproducto') {
                                  return 'Arriendo';
                                } else if (
                                  rowData.descripcion?.toLowerCase().includes('habilitacion') ||
                                  rowData.descripcion?.toLowerCase().includes('obra civil')
                                ) {
                                  return 'Servicio único';
                                } else {
                                  return 'Servicio';
                                }
                              }}
                            />

                            <Column
                              field="consolidado"
                              header="Doc. Unificado"
                              body={(rowData) => (
                                <div className="flex justify-content-center">
                                  <span style={{ fontSize: '12px', marginRight: '6px' }}>
                                    {!rowData.consolidado ? 'NO' : ' '}
                                  </span>
                                  <InputSwitch
                                    checked={rowData.consolidado}
                                    onChange={e => onToggleConsolidado(rowData, e.value)}
                                    disabled={cobroCounts[rowData.cobro] === 1}
                                  />
                                  <span style={{ fontSize: '12px', marginLeft: '6px' }}>
                                    {rowData.consolidado ? 'SI' : ' '}
                                  </span>
                                </div>
                              )}
                            />

                            <Column
                              field="agrupamiento"
                              header="N° Documento"
                              body={(rowData) => (
                                <div className="flex justify-content-center">
                                  <Dropdown
                                    value={
                                      (rowData.agrupamiento > 0 ? rowData.agrupamiento : null)
                                    }
                                    options={optionsByCobro[rowData.cobro]}
                                    onChange={(e) => onToggleAgrupamiento(rowData, e)}
                                    placeholder="Seleccionar"
                                    disabled={
                                      cobroCounts[rowData.cobro] === 1 ||
                                      (!rowData.consolidado && detallesCobro.filter(i => i.agrupamiento === rowData.agrupamiento).length === 1) ||
                                      (!rowData.consolidado)
                                    }
                                  />
                                </div>
                              )}
                            />
                          </DataTable>
                        </div>

                      </fieldset>
                      
                    </div>
                    <div className="col-12 responsive-dialog-buttons" style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: '1rem' }}>
                      <Button
                        label="Volver"
                        icon="pi pi-arrow-left"
                        className="p-button-warning mt-4 mb-4 p-button-rounded p-button-md p-button"
                        onClick={() => {
                          if (window.confirm("¿Está seguro que desea volver? Se perderá la configuración ingresada.")) {
                            setShowCerrarContrato(false);
                          }
                        }}
                        style={{color: "white" }}
                        disabled={loadingAsig}
                        raised
                      />

                      <Button
                        label="Asignar"
                        icon={loadingAsig ? "pi pi-spin pi-spinner" : "pi pi-check"}
                        className="p-button-success mt-4 mb-4 p-button-rounded p-button-md p-button"
                        onClick={() => {
                          handleValidar();
                        }}
                        disabled={loadingAsig}
                        raised
                      />
                    </div>
                  </div>
                </>
              )}
            </>
          </Dialog>
        </>
      )}

      {estadoValido && estadoActual === "ASIGNACIÓN" && (
        <>
          <div className="col-12 md:col-3"></div>
          {mostrarModificarAsignacion ? (
            <div className="col-12 md:col-2">
              <Button
                label="Modificar Asignación"
                icon="pi pi-cog"
                className="p-button-secondary mt-4 mb-4 p-button-rounded p-button"
                onClick={handleShowModificarAsignacion}
                style={{ width: "100%", height: "50px" }}
                disabled={loading}
                raised
              />
            </div>
          ): <div className="col-12 md:col-2"></div> }
          <div className="col-12 md:col-2">
            <Button
              label="Ver Resumen Mensual"
              icon="pi pi-calendar"
              onClick={handleShowResumenMensual}
              className="p-button-help mt-4 mb-4 p-button-rounded p-button"
              style={{ width: "100%", height: "50px" }}
              disabled={loading}
              raised
            />
          </div>
          {mostrarModificarAsignacion && mostrarRenovarAsignacion ? (
            <div className="col-12 md:col-2">
              <Button
                label="Renovar Asignación"
                icon="pi pi-forward"
                className="p-button-info mt-4 mb-4 p-button-rounded p-button-md p-button"
                onClick={handleShowRenovarAsignacion}
                style={{ width: "100%", height: "50px" }}
                disabled={loading}
                raised
              />
            </div>
          ): <div className="col-12 md:col-2"></div> }
        </>
      )}
    </div>
  );
};

export default CotizacionActions;
